import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import { useAuthContext } from '@lp/core/auth';
import {
  OpoContactForm,
  CREATE_OPO_CONTACT_FORM_DEFAULT_VALUES,
  createOpoContactFormSchema,
  CreateOpoContactFormValues,
} from '@lp/features/admin/forms/OpoContactForm';
import { retrieveUnifiedError } from '@/api/utils';
import { FileType } from '@/queries/file';
import { useCreateOpoContactMutation, useCreateOpoUserMutation } from '@/queries/opo';
import { useUploadFile } from '@/hooks/useUploadFile';
import { AdminPermissions } from '@/permissions';
import { CreateOpoUserRequestBody } from '@/api/apis/opo';

export const OpoContactCreate = () => {
  const navigate = useNavigate();
  const { mutateAsync: mutateAsyncOpoContact } = useCreateOpoContactMutation();
  const { mutateAsync: mutateAsyncOpoUser } = useCreateOpoUserMutation();
  const { uploadFiles } = useUploadFile(FileType.Opo);
  const { checkPermission } = useAuthContext();

  const hasOpoCreatePermissions = checkPermission(AdminPermissions.OpoCreate);

  const handleBackToList = () => {
    navigate(`/admin/opos/contacts`);
  };

  const handleOpoSubmit = async (data: CreateOpoContactFormValues) => {
    try {
      let uploadedFile;

      if (data.photo) {
        uploadedFile = await uploadFiles([data.photo]);
      }

      const requestBody = {
        firstName: data.firstName || null,
        lastName: data.lastName || null,
        primaryPhone: data.primaryPhone || null,
        photoPath: uploadedFile ? uploadedFile[0].fileName : (data.photoPath ?? null),
        email: data.email || null,
        secondaryPhone: data.secondaryPhone || null,
        memberships: data.memberships.map(({ opoOrganizationId, roles }) => ({ opoOrganizationId, roles })),
      };

      if (data.inviteAsUser && data.email && data.firstName && data.lastName) {
        await mutateAsyncOpoUser(requestBody as CreateOpoUserRequestBody);
      } else {
        await mutateAsyncOpoContact(requestBody);
      }

      toast.success('OPO Contact created successfully.');
      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Form<CreateOpoContactFormValues>
      defaultValues={CREATE_OPO_CONTACT_FORM_DEFAULT_VALUES}
      schema={createOpoContactFormSchema}
      onSubmit={handleOpoSubmit}
    >
      <div className="flex flex-col gap-9">
        <AdminPageHeader title="Create OPO Contact" backToListLink="/admin/opos/contacts">
          <AdminFormAction
            type="create"
            handleClick={handleBackToList}
            hasCreatePermissions={hasOpoCreatePermissions}
          />
        </AdminPageHeader>
        <OpoContactForm isReadOnly={!hasOpoCreatePermissions} />
      </div>
    </Form>
  );
};
